import {
  WS_PING_INTERVAL_MS,
  WS_PONG_TIMEOUT_MS,
  WS_RECONNECT_BASE_MS,
  WS_RECONNECT_MAX_MS,
  WS_RECONNECT_MAX_TRIES,
  type ClientMessage,
  type ServerMessage,
} from '@tarot/shared';

/**
 * RoomDO WebSocket 래퍼. 메시지 타입은 @tarot/shared 계약 사용.
 *
 * 모바일에서 방을 열어둔 채 카카오톡 등으로 나갔다 오면 연결이 죽어 있는 경우가 많다.
 * 원인이 두 가지라 대응도 두 가지다:
 *  1. **조용한 연결이 끊긴다** — 방은 카드를 뽑기 전까지 오가는 메시지가 없어서,
 *     이동통신 NAT·프록시가 유휴 TCP를 정리해 버린다 → 주기적 ping으로 살려 둔다
 *  2. **끊긴 걸 브라우저가 안 알려준다** — readyState는 OPEN인데 실제로는 죽은
 *     half-open 상태가 흔하다 → pong이 제때 안 오면 죽은 것으로 보고 다시 붙는다
 *
 * 여기에 더해 화면으로 돌아온 순간(visibilitychange)과 네트워크 복구(online)에는
 * 백오프를 기다리지 않고 즉시 확인한다. 사용자가 체감하는 건 결국 이 타이밍이다.
 */
export interface RoomSocketHandlers {
  onMessage: (msg: ServerMessage) => void;
  /** 연결될 때마다 호출 — 최초 1회가 아니라 **재연결마다** 불린다 (seat:join 재전송용) */
  onOpen?: () => void;
  /** 끊겨서 다시 붙는 중 — 사용자에겐 "다시 연결 중" 정도만 알리면 된다 */
  onReconnecting?: (attempt: number) => void;
  /** 재시도를 모두 소진했거나 사용자가 닫음 — 여기서 처음으로 "끊겼다"고 말한다 */
  onGaveUp?: () => void;
}

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

export class RoomSocket {
  private ws: WebSocket | null = null;
  private closedByUser = false;
  /**
   * 한 번이라도 좌석을 받은 뒤에만 자동 재연결한다.
   * 없는 방·만료된 방은 연결 자체가 실패하는데, 그걸 8번 재시도하면 사용자는 30초를 멍하니
   * 기다린 뒤에야 안내를 본다. 첫 연결 실패는 그냥 실패로 알린다.
   */
  private armed = false;
  private attempt = 0;
  private pingTimer: ReturnType<typeof setInterval> | undefined;
  private pongTimer: ReturnType<typeof setTimeout> | undefined;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private roomId: string,
    private handlers: RoomSocketHandlers,
  ) {}

  connect() {
    this.closedByUser = false;
    document.addEventListener('visibilitychange', this.onWake);
    window.addEventListener('online', this.onWake);
    this.open();
  }

  private open() {
    const httpBase = BASE.startsWith('http') ? BASE : `${location.origin}${BASE}`;
    const wsBase = httpBase.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/rooms/${this.roomId}/ws`);
    this.ws = ws;

    ws.onopen = () => {
      if (ws !== this.ws) return; // 늦게 열린 옛 소켓 — 무시
      this.attempt = 0;
      this.startKeepalive();
      this.handlers.onOpen?.();
    };
    ws.onmessage = (e) => {
      if (ws !== this.ws) return;
      let msg: ServerMessage;
      try {
        msg = JSON.parse(String(e.data)) as ServerMessage;
      } catch {
        return; // 알 수 없는 프레임 — 무시
      }
      // 어떤 응답이든 연결이 살아 있다는 증거다. pong만 기다릴 이유가 없다
      clearTimeout(this.pongTimer);
      if (msg.type === 'pong') return;
      this.handlers.onMessage(msg);
    };
    ws.onclose = () => {
      if (ws !== this.ws || this.closedByUser) return;
      this.stopKeepalive();
      this.scheduleRetry();
    };
    ws.onerror = () => {
      // onerror 뒤에는 onclose가 따라오지만, 안 오는 브라우저가 있어 직접 닫는다
      if (ws === this.ws && !this.closedByUser) ws.close();
    };
  }

  /** 유휴 연결이 중간 장비에 끊기지 않게 ping을 보내고, pong이 없으면 죽은 것으로 본다 */
  private startKeepalive() {
    this.stopKeepalive();
    this.pingTimer = setInterval(() => this.probe(), WS_PING_INTERVAL_MS);
  }

  private stopKeepalive() {
    clearInterval(this.pingTimer);
    clearTimeout(this.pongTimer);
    this.pingTimer = undefined;
    this.pongTimer = undefined;
  }

  private probe() {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ type: 'ping' } satisfies ClientMessage));
    clearTimeout(this.pongTimer);
    this.pongTimer = setTimeout(() => {
      // 살아 있다고 표시돼 있지만 응답이 없다 — 끊고 다시 붙는다
      if (this.closedByUser) return;
      this.forceReconnect();
    }, WS_PONG_TIMEOUT_MS);
  }

  /** 죽은 소켓을 버리고 즉시 새로 연결 (onclose 경로를 타지 않도록 참조를 먼저 끊는다) */
  private forceReconnect() {
    const dead = this.ws;
    this.ws = null;
    this.stopKeepalive();
    try {
      dead?.close();
    } catch {
      /* 이미 닫힌 소켓 */
    }
    this.scheduleRetry();
  }

  /** 좌석을 받았다 — 이제부터는 끊겨도 스스로 다시 붙는다 */
  arm() {
    this.armed = true;
  }

  private scheduleRetry() {
    if (this.closedByUser) return;
    clearTimeout(this.retryTimer);
    if (!this.armed || this.attempt >= WS_RECONNECT_MAX_TRIES) {
      // 포기했다고 알리되 **깨우기 리스너는 남겨둔다** — 사용자가 화면으로 돌아오거나
      // 네트워크가 살아나면 onWake가 다시 붙는다. 안내 화면이 저절로 걷히는 게 정상이다.
      this.handlers.onGaveUp?.();
      return;
    }
    this.attempt += 1;
    this.handlers.onReconnecting?.(this.attempt);
    const wait = Math.min(WS_RECONNECT_BASE_MS * 2 ** (this.attempt - 1), WS_RECONNECT_MAX_MS);
    this.retryTimer = setTimeout(() => {
      if (!this.closedByUser) this.open();
    }, wait);
  }

  /**
   * 화면으로 돌아왔거나 네트워크가 살아났을 때 — 백오프를 기다리지 않고 지금 확인한다.
   * 열려 있어 보여도 곧장 ping을 쏴서, 죽은 채 OPEN으로 남은 소켓을 바로 잡아낸다.
   */
  private onWake = () => {
    if (this.closedByUser || document.visibilityState === 'hidden') return;
    if (this.ws?.readyState === WebSocket.OPEN) return this.probe();
    if (!this.armed) return;
    if (this.ws?.readyState === WebSocket.CONNECTING) return;
    clearTimeout(this.retryTimer);
    this.attempt = 0; // 사용자가 돌아온 시점은 새 출발로 본다
    this.handlers.onReconnecting?.(1);
    this.open();
  };

  private stopWaking() {
    document.removeEventListener('visibilitychange', this.onWake);
    window.removeEventListener('online', this.onWake);
  }

  send(msg: ClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  close() {
    this.closedByUser = true;
    this.stopKeepalive();
    this.stopWaking();
    clearTimeout(this.retryTimer);
    try {
      this.ws?.close();
    } catch {
      /* 이미 닫힌 소켓 */
    }
    this.ws = null;
  }
}
